import Indicator from '../Indicator';
import LineMarker from '../markers/LineMarker';

/** Ichimoku Cloud indicator */
export default class IchimokuCloudIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} senkouCloudOnly - Whether to show only the cloud
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		// terminology:
		//
		// chikouspan = lagging span
		// senkou a/b = leading spans
		// kijunsen = base line
		// tenkansen = conversion line

		return {
			id: 'ichimokucloud',
			name: 'Ichimoku Cloud',
			description: 'The Ichimoku Cloud is a comprehensive technical analysis tool that provides information about support and resistance levels, trend direction, and momentum, using multiple averages to create a "cloud" that helps forecast price movements.',
			senkouCloudOnly: false, // sometimes users only want the cloud
			inputs: [
				{ name: 'fastPeriod', value: 9 },
				{ name: 'mediumPeriod', value: 26 },
				{ name: 'slowPeriod', value: 52 },
				{ name: 'displacementCloud', value: 26 }
			],
			componentSeries: [
				{
					id: 'senkouspana',
					styleRef: 'senkou.a'
				},
				{
					id: 'senkouspanb',
					styleRef: 'senkou.b'
				},
				{
					id: 'chikouspan',
					styleRef: 'chikou'
				},
				{
					id: 'kijunsen',
					styleRef: 'kijun'
				},
				{
					id: 'tenkansen',
					styleRef: 'tenkan'
				}
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const vals = [];

		for (const row of data.ichimokucloud) {
			if (!isNaN(row.chikouspan)) {
				vals.push(row.chikouspan);
			}
			if (!isNaN(row.kijunsen)) {
				vals.push(row.kijunsen);
			}
			if (!isNaN(row.senkouspana)) {
				vals.push(row.senkouspana);
			}
			if (!isNaN(row.senkouspanb)) {
				vals.push(row.senkouspanb);
			}
			if (!isNaN(row.tenkansen)) {
				vals.push(row.tenkansen);
			}
		}

		return Math.min.apply(null, vals);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const vals = [];

		for (const row of data.ichimokucloud) {
			if (!isNaN(row.chikouspan)) {
				vals.push(row.chikouspan);
			}
			if (!isNaN(row.kijunsen)) {
				vals.push(row.kijunsen);
			}
			if (!isNaN(row.senkouspana)) {
				vals.push(row.senkouspana);
			}
			if (!isNaN(row.senkouspanb)) {
				vals.push(row.senkouspanb);
			}
			if (!isNaN(row.tenkansen)) {
				vals.push(row.tenkansen);
			}
		}

		return Math.max.apply(null, vals);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} line
	 */
	_getMarkers() {
		return (
			this.markers || {
				line: new LineMarker()
			}
		);
	}

	/**
	 * @param {object} data - Indicator data
	 * @param {string} type - Indicator type
	 * @return {number[][]}
	 */
	_getDataMap(data, type) {
		// use an alternate date index (dateIndexChikou) for the chikou series only (CW-3069)
		if (type === 'chikouspan' && data.length > 1) {
			return data
				.map((d, idx) => {
					if (typeof d.dateIndexChikou === 'number') {
						// chikou dates can exceed the left XAxis boundary on some "Max" requests, leading
						// to multiple dateIndexChikou values snapping to 0.  Discard the duplicates to prevent
						// multiple points stacking up at the same position.
						if (
							d.dateIndexChikou > 0 ||
							data[idx + 1].dateIndexChikou !== 0
						) {
							return [d.dateIndexChikou, +d['chikouspan']];
						}
					}
					return false;
				})
				.filter(row => row !== undefined); // trim out undefined's
		}

		// all other series
		let prevDateIndex = null;
		return data
			.map(d => {
				// don't allow more than one datapoint per dateIndex.  this can happen
				// with senkou datasets on monthly frequencies, which plot into the future
				// and sometimes exceed the date of the right-hand edge of the XAxis.
				if (!prevDateIndex || d.dateIndex !== prevDateIndex) {
					prevDateIndex = d.dateIndex;
					return [d.dateIndex, +d[type]];
				}
				return false;
			})
			.filter(row => row !== undefined); // trim out undefined's
	}

	/**
	 * Gets the regions to clip
	 * @param {number} xScale
	 * @param {array} dataUpper - Upper region data
	 * @param {array} dataLower - Lower region data
	 * @returns {array[]}
	 */
	_getClipRegions(xScale, dataUpper, dataLower) {
		const regions = [];
		let newRegion = {};
		let x = 0;

		mainLoop: for (
			x = 0;
			x < Math.min(dataUpper.length, dataLower.length);
			x++
		) {
			if (dataUpper[x][1] >= dataLower[x][1]) {
				// begin new region
				newRegion = [x, x];

				for (x++; x < dataUpper.length; x++) {
					if (dataUpper[x][1] >= dataLower[x][1]) {
						// widen region by 1
						newRegion[1] = x;

						if (x === dataUpper.length - 1) {
							// reached end of data, finalize region
							regions.push(newRegion);
						}
					} else {
						// line crossed back under, finalize region
						regions.push(newRegion);
						continue mainLoop;
					}
				}
			}
		}

		return regions;
	}

	/**
	 * Returns the x coordinate for a line cross
	 * @param {number} dateIndex
	 */
	_getX(dateIndex) {
		return this.xScale(dateIndex);
	}

	/**
	 * Returns the y coordinate for a line cross
	 * @param {number} dateIndex
	 */
	_getY(dateIndex) {
		return this.yScale(dateIndex);
	}

	/**
	 * Renders clip regions
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[][]} regions - Array of clip regions
	 * @param {number[][]} dataUpper - Upper region data
	 * @param {number[][]} dataLower - Lower region data
	 * @param {boolean} isPos
	 */
	_renderClipRegions(ctx, regions, dataUpper, dataLower, isPos) {
		const debugRegions = false; // set to true to draw rectangles representing each clip region
		const self = this;
		let currTop = [];
		let currBot = [];
		const height = this.panel.size.height;
		let neighborIdx = 0;
		let neighborUpper = 0;
		let neighborLower = 0;
		let xIntersect = 0;
		let left = 0;
		let right = 0;
		let x1 = 0;
		let y1 = 0;
		let x2 = 0;
		let y2 = 0;
		let x3 = 0;
		let y3 = 0;
		let x4 = 0;
		let y4 = 0;

		ctx.beginPath();

		regions.forEach(r => {
			// find left edge of region for top and bottom
			currTop = dataUpper[r[0]];
			currBot = dataLower[r[0]];

			// find preceding index
			neighborIdx = r[0] - 1;

			// clamp to left edge of data if needed
			if (typeof dataUpper[neighborIdx] === 'undefined') {
				neighborIdx = r[0];
			}

			// find left neighbor
			neighborUpper = dataUpper[neighborIdx];
			neighborLower = dataLower[neighborIdx];

			// get xy coords of crossing lines
			x1 = self._getX(neighborUpper[0]);
			y1 = self._getY(neighborUpper[1]);
			x2 = self._getX(currTop[0]);
			y2 = self._getY(currTop[1]);
			x3 = self._getX(neighborLower[0]);
			y3 = self._getY(neighborLower[1]);
			x4 = self._getX(currBot[0]);
			y4 = self._getY(currBot[1]);

			// find x intersection with left neighbor
			xIntersect = self._getIntersection(x1, y1, x2, y2, x3, y3, x4, y4);

			// set new region left coord
			left = xIntersect === null ? x2 : xIntersect;

			// find right edge of region for top and bottom
			currTop = dataUpper[r[1]];
			currBot = dataLower[r[1]];

			// find following index
			neighborIdx = r[1] + 1;

			// clamp to right edge of data if needed
			if (typeof dataUpper[neighborIdx] === 'undefined') {
				neighborIdx = r[1];
			}

			// find right neighbor
			neighborUpper = dataUpper[neighborIdx];
			neighborLower = dataLower[neighborIdx];

			// get xy coords of crossing lines
			x1 = self._getX(currTop[0]);
			y1 = self._getY(currTop[1]);
			x2 = self._getX(neighborUpper[0]);
			y2 = self._getY(neighborUpper[1]);
			x3 = self._getX(currBot[0]);
			y3 = self._getY(currBot[1]);
			x4 = self._getX(neighborLower[0]);
			y4 = self._getY(neighborLower[1]);

			// find x intersection with right neighbor
			xIntersect = self._getIntersection(x1, y1, x2, y2, x3, y3, x4, y4);

			// set new region right coord
			right = xIntersect === null ? x1 : xIntersect;

			// draw rect
			ctx.rect(left, 0, right - left, height);
		});

		if (debugRegions) {
			ctx.fillStyle = isPos
				? 'rgba(64,255,239,0.2)'
				: 'rgba(255,50,50,0.2)';
			ctx.fill();
		}

		ctx.clip();
	}

	/**
	 * Returns the intersection point
	 * @param {number} x1 - Point 1 x coordinate
	 * @param {number} y1 - Point 1 y coordinate
	 * @param {number} x2 - Point 2 x coordinate
	 * @param {number} y2 - Point 2 y coordinate
	 * @param {number} x3 - Point 3 x coordinate
	 * @param {number} y3 - Point 3 y coordinate
	 * @param {number} x4 - Point 4 x coordinate
	 * @param {number} y4 - Point 4 y coordinate
	 * @return {number}
	 */
	_getIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
		let ua = 0.0;
		const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

		if (denom === 0) {
			return null;
		}

		ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;

		return x1 + ua * (x2 - x1);
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const ctx = this.panel.rootContext;
		const xScale = this.xScale;
		const yScale = this.yScale;
		let dataSenkouA = this._getDataMap(
			data.ichimokucloud || [],
			'senkouspana'
		);
		let dataSenkouB = this._getDataMap(
			data.ichimokucloud || [],
			'senkouspanb'
		);
		const dataChikou = this._getDataMap(
			data.ichimokucloud || [],
			'chikouspan'
		);
		const dataKijun = this._getDataMap(data.ichimokucloud, 'kijunsen');
		const dataTenkan = this._getDataMap(data.ichimokucloud, 'tenkansen');

		// clean up empty points
		dataSenkouA = dataSenkouA.filter(row => !isNaN(row[1]));
		dataSenkouB = dataSenkouB.filter(row => !isNaN(row[1]));

		// trim SenkouA to match SenkouB
		dataSenkouA = dataSenkouA.splice(
			dataSenkouA.length - dataSenkouB.length,
			dataSenkouA.length
		);

		// get clip regions
		const regionsPos = this._getClipRegions(xScale, dataSenkouA, dataSenkouB);
		const regionsNeg = this._getClipRegions(xScale, dataSenkouB, dataSenkouA);

		// neg fill
		ctx.save();

		// clips for neg only
		this._renderClipRegions(ctx, regionsNeg, dataSenkouA, dataSenkouB);

		// neg fill
		this.markers.line.renderFillBetween(
			ctx,
			dataSenkouA,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.negative.color
		);

		// restore context
		ctx.restore();

		ctx.save();

		// clips for pos only
		this._renderClipRegions(ctx, regionsPos, dataSenkouA, dataSenkouB, true);

		// pos fill
		this.markers.line.renderFillBetween(
			ctx,
			dataSenkouA,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.positive.color
		);

		// restore context
		ctx.restore();

		// dataSenkouB line
		this.markers.line.render(
			ctx,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.b.color,
			this.params.style.senkou.b.width,
			this.params.style.senkou.b.lineDash
		);

		// dataSenkouA line
		this.markers.line.render(
			ctx,
			dataSenkouA,
			xScale,
			yScale,
			this.params.style.senkou.a.color,
			this.params.style.senkou.a.width,
			this.params.style.senkou.a.lineDash
		);

		if (!this.params.senkouCloudOnly) {
			// kijun line
			this.markers.line.render(
				ctx,
				dataKijun,
				xScale,
				yScale,
				this.params.style.kijun.color,
				this.params.style.kijun.width,
				this.params.style.kijun.lineDash
			);

			// tenkan line
			this.markers.line.render(
				ctx,
				dataTenkan,
				xScale,
				yScale,
				this.params.style.tenkan.color,
				this.params.style.tenkan.width,
				this.params.style.tenkan.lineDash
			);

			// chikou line
			this.markers.line.render(
				ctx,
				dataChikou,
				xScale,
				yScale,
				this.params.style.chikou.color,
				this.params.style.chikou.width,
				this.params.style.chikou.lineDash
			);
		}
	}
}
